import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateTruckTypeMutation } from '../../../../hooks/mutations/useTruckTypeMutation';
import { useTrucksTypesQuery } from '../../../../hooks/queries/useTrucksTypesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { TruckType } from '../../../../services/apiSLTP/materials/trucksTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import {
    crudStatus,
    getCrudStatusInfos,
    getPriceTypeInfos,
    getTruckGroupInfos,
    localStorageKeys,
    permissionLevels,
    systemConfigKeyEnum,
} from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import IconLine from '../../../IconLine';
import UpdateTruckTypeModal from '../../../modals/admin/vehicles/UpdateTruckTypeModal';
import DeleteModal from '../../../modals/DeleteModal';
import { InheritedPriceDisplay } from '../../../InheritedPriceDisplay';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import GenericAgGridTable from '../../GenericAgGridTable';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import IconButton from '../../../shared/IconButton';
import { useTheme } from '../../../../hooks/useTheme';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface TrucksTypesSettingsRowProps {
    withUsageCount: boolean;
}

const TrucksTypesSettingsRow: React.FC<TrucksTypesSettingsRowProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();
    const { muiTheme } = useTheme();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTruckType, setSelectedTruckType] = useState<TruckType | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: dataTruckTypes, isLoading, refetch } = useTrucksTypesQuery({ queries: { withUsage: withUsageCount } });

    const { mutate: mutateTruckType } = useUpdateTruckTypeMutation({
        onSuccess: () => {
            refetch();
        },
        onError: (error) => {
            console.error('Error updating truck type:', error.message);
        },
    });

    const columnDefs: ColDef<TruckType>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<TruckType>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyTruckType'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedTruckType(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteTruckType'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedTruckType(params.data);
                                    setIsEditMode(false);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<TruckType>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('group')),
            valueGetter: (params) => (params.data?.group ? capitalizeFirstLetter(t(getTruckGroupInfos(params.data?.group))) : null),
            colId: 'group',
        },
        {
            headerName: capitalizeFirstLetter(t('dumpAllowed')),
            field: 'isDumpAllowed',
            colId: 'isDumpAllowed',
            cellRenderer: 'agCheckboxCellRenderer',
        },
        {
            headerName: capitalizeFirstLetter(t('defaultTonnage')),
            valueFormatter: (params) => (params.value ? formatNumber(params.value, t('locale')) : ''),
            field: 'defaultTonnage',
            cellStyle: (params) => (params.data?.defaultTonnage === 0 ? { color: muiTheme.palette.error.main } : undefined),
            colId: 'defaultTonnage',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('minTonnage')),
            valueFormatter: (params) => (params.value ? formatNumber(params.value, t('locale')) : ''),
            field: 'minTonnage',
            cellStyle: (params) => (params.data?.minTonnage === 0 ? { color: muiTheme.palette.error.main } : undefined),
            colId: 'minTonnage',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('maxTonnage')),
            valueFormatter: (params) => (params.value ? formatNumber(params.value, t('locale')) : ''),
            field: 'maxTonnage',
            cellStyle: (params) => (params.data?.maxTonnage === 0 ? { color: muiTheme.palette.error.main } : undefined),
            colId: 'maxTonnage',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('price') + ` (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`),
            valueFormatter: (params) => formatNumber(params.value, t('locale')),
            field: 'price',
            cellStyle: (params) => (params.data?.price === 0 ? { color: muiTheme.palette.error.main } : undefined),
            colId: 'price',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeInfos(params.data?.priceType))),
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('displayOrder')),
            field: 'displayOrder',
            colId: 'displayOrder',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        createDateTimeColumn<TruckType>('createdAt', 'createdAt'),
        createDateTimeColumn<TruckType>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
                colId: '_id',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xtotal',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xRidesPlanned')),
                field: 'usageCount.dailyTrucksPlanned',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xridesplanned',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('trucks')),
                field: 'usageCount.trucks',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'trucks',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    return (
        <>
            <GenericAgGridTable
                rowData={dataTruckTypes || []}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName="admin-truck-types"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedTruckType(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addTruckType'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.VEHICLES.TRUCKTYPES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
            {showEditModal && (
                <UpdateTruckTypeModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    truckType={selectedTruckType}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}
            {showDeleteModal && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        if (selectedTruckType) {
                            mutateTruckType({ truckTypeId: selectedTruckType._id, data: { crudStatus: crudStatus.DELETED } });
                        }
                    }}
                    title={t('truckType')}
                    itemName={selectedTruckType?.designation}
                />
            )}
        </>
    );
};

export default TrucksTypesSettingsRow;
